import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/site/SeoHead";

const CONTENT = {
  faq: [
    ["When will my order ship?", "Every MELAVI piece is small-batch made in Bengaluru. We dispatch within 5 working days of your order. You'll receive tracking on email as soon as it leaves the studio."],
    ["Do you deliver to my city?", "Yes — we ship pan-India via reliable couriers. Cash-on-delivery is available in select metros (Bengaluru, Mumbai, Delhi NCR, Chennai, Hyderabad, Pune, Kolkata, Ahmedabad). Every other pincode is prepaid via Razorpay (UPI, cards, netbanking, wallets)."],
    ["How much is shipping?", "Free shipping across India on orders over ₹1,499. Below that, a flat ₹79 is added at checkout."],
    ["How do I care for my MELAVI piece?", "Gentle cold-water hand wash. Dry in shade. Iron inside-out on medium. Store folded, away from direct sun. Every piece softens beautifully with wear."],
    ["Can I exchange sizes?", "Yes, within 7 days of delivery — in original packaging, unwashed and unworn. Write to Melavi.in@gmail.com with your order number and we'll arrange a pickup."],
    ["Are sale items returnable?", "Sale items are final sale. Please refer to the size chart on the product page before ordering."],
    ["Do you offer gift wrapping?", "Every MELAVI order is packed in a linen pouch with a hand-tied ribbon and a note-card slot. Add a gift message in the checkout notes field."],
    ["Where are you based?", "MeLaVi Creations · #1991, JP Nagar 2nd Phase, Bengaluru 560078 · GSTIN 29ACCFM9675R1Z0."],
  ],
  terms: {
    intro: "These Terms & Conditions govern your use of the MELAVI website (melavi.in) and the purchase of our products. By placing an order you accept these terms in full. Products are sold by MeLaVi Creations, GSTIN 29ACCFM9675R1Z0.",
    sections: [
      ["Orders & pricing", "All prices are in Indian Rupees (INR) and inclusive of GST. Availability is confirmed at the point of dispatch. We reserve the right to refuse or cancel an order in case of pricing errors, stock shortages, or suspected fraudulent activity, with a full refund issued in that case."],
      ["Payments", "Prepaid orders are processed via Razorpay (UPI, cards, netbanking, wallets). Cash-on-delivery is available at select pincodes only. All transactions are secured; MELAVI does not store card or UPI details on its servers."],
      ["Shipping", "We dispatch within 5 working days. Delivery windows are estimates; delays due to courier, weather, or force majeure events are outside our control."],
      ["Returns & exchanges", "Please see the Returns page. Sale items are final sale."],
      ["Intellectual property", "All artwork, motifs, embroidery designs, product photography, and copy on this site are the property of MeLaVi Creations. No part may be reproduced without written permission."],
      ["Jurisdiction", "These terms are governed by the laws of India. Any dispute is subject to the exclusive jurisdiction of the courts at Bengaluru, Karnataka."],
    ],
  },
  returns: {
    intro: "We hand-make every piece slowly, and we want it to fit into your world just as slowly. If a size or fit is off, we'll happily exchange or refund within 7 days of delivery.",
    sections: [
      ["Eligibility", "Items must be unworn, unwashed, and in original packaging with all tags intact. Any piece that has been altered, embroidered further, or damaged in use is not eligible."],
      ["How to initiate", "Write to Melavi.in@gmail.com with your order number and photographs within 7 days of delivery. We'll arrange a reverse-pickup at no cost to you."],
      ["Exchanges", "First exchange is free. We'll swap for the requested size subject to availability, or issue a store credit valid for 12 months if the size is no longer in stock."],
      ["Refunds", "Refunds are issued to the original payment method within 7 working days of the returned piece reaching our studio. For COD orders, we'll refund via bank transfer or UPI on request."],
      ["Not returnable", "Sale items, custom-made pieces, and items marked 'final sale' are not returnable."],
    ],
  },
  privacy: {
    intro: "MELAVI (MeLaVi Creations) respects your privacy. This policy explains what data we collect, why, and how you can control it.",
    sections: [
      ["What we collect", "Name, email, phone, delivery address, order history, and cart contents. When you write to us, we keep the message thread for support continuity."],
      ["Why we collect it", "To fulfil your order, communicate about it, and improve the site. We do not build advertising profiles."],
      ["Payments", "Payment details (card / UPI / netbanking) are handled entirely by Razorpay. MELAVI never sees, stores, or logs your payment credentials."],
      ["Cookies", "We use minimal cookies to remember your cart between visits and to keep you logged in as an admin (if you are one). We do not use third-party ad trackers."],
      ["Sharing", "We share your address and phone with our courier partner strictly for delivery. We never sell or rent your data."],
      ["Your rights", "Write to Melavi.in@gmail.com to request a copy of your data, correct it, or delete it. We'll respond within 7 working days."],
    ],
  },
  tracking: {
    intro: "Order tracking is on its way. For now, please look for the tracking link in your dispatch email (subject line: 'MELAVI · Order MLxxxx is on its way'), or reach us directly.",
    sections: [
      ["Need help right now?", "Email Melavi.in@gmail.com with your order number, or WhatsApp us at +91 81235 41555. Meghana or Lallitha will reply, personally."],
    ],
  },
  wishlist: {
    intro: "Your wishlist lives here. Save pieces you love — we'll keep them across visits.",
    sections: [
      ["Coming soon", "Signed-in wishlists and back-in-stock alerts are on the roadmap. For now, browse the shop and add pieces to your cart to hold them for the session."],
    ],
  },
  account: {
    intro: "Your MELAVI account, coming soon. For now, guest checkout keeps things simple — no passwords to remember, no forms to save.",
    sections: [
      ["What's next", "Sign-in with email or phone, order history, saved addresses, and wishlist syncing are being built. Watch this space."],
    ],
  },
};

const SIMPLE_META = {
  faq: { path: "/frequently-asked-questions", desc: "Answers on MELAVI shipping, sizing, exchanges, care, and custom orders." },
  terms: { path: "/terms-conditions", desc: "Terms & Conditions for purchases, returns, payments and IP on melavi.in — MeLaVi Creations, Bengaluru." },
  returns: { path: "/return", desc: "MELAVI returns and exchanges policy — 7-day exchange window, free first exchange, refund flow." },
  privacy: { path: "/privacy-policy", desc: "How MELAVI collects, stores and uses your data. No advertising trackers. Payment data stays with Razorpay." },
  tracking: { path: "/tracking-order", desc: "Track your MELAVI order — dispatch details, tracking link, and studio contact." },
  wishlist: { path: "/wishlist", desc: "Your MELAVI wishlist — save pieces you love and return to them." },
  account: { path: "/my-account", desc: "Your MELAVI account — order history, saved addresses, and preferences." },
};

const Simple = ({ title, kind }) => {
  const data = CONTENT[kind];
  const isFaq = kind === "faq";
  const meta = SIMPLE_META[kind] || {};
  return (
    <div className="pt-32 md:pt-40 pb-32" data-testid={`simple-${kind}`}>
      <SeoHead title={title} description={meta.desc || `${title} · MELAVI`} path={meta.path} />
      <div className="max-w-[900px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="nav-item mb-4 text-ink/60">MELAVI</div>
        <h1 className="serif text-5xl md:text-6xl leading-[1.02] mb-12">{title}</h1>

        {isFaq ? (
          <div className="space-y-8">
            {data.map(([q, a], i) => (
              <div key={i} className="hairline-b pb-6">
                <h3 className="serif text-2xl">{q}</h3>
                <p className="mt-3 text-ink/80 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        ) : (
          <>
            <p className="text-ink/80 leading-relaxed text-lg mb-12">{data.intro}</p>
            <div className="space-y-8">
              {data.sections.map(([h, body], i) => (
                <div key={i} className="hairline-b pb-6">
                  <h3 className="serif text-2xl">{h}</h3>
                  <p className="mt-3 text-ink/80 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="mt-12">
          <Link to="/contact-us" className="crimson-link nav-item !text-[12px]">Have another question? Write to us →</Link>
        </div>
      </div>
    </div>
  );
};

export default Simple;
